import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Node } from "@xyflow/react";

import { SubjectEditor } from "@/pages/Private/pages/Account/components/SubjectEditor";

import { NodeData } from "../pages/CampaignPage";
import { EventOutreachSchemaType } from "../../../schema/list";
import { ColumnConfig } from "../../../schema/columnConfig";
import { resolveInheritedReplySubject } from "../utils/resolveEmailSubject";

interface CampaignEmailSubjectEditorProps {
	nodeId: string;
	nodeType: string;
	subject: string;
	hasPrecedingEmailNode: boolean;
	eventBasedState: boolean;
	getPreviousNode: (id: string) => Node<NodeData> | null;
	onChange: (value: string) => void;
	onInputChange?: (value: boolean) => void;
	currentLeadIndex: number;
	listColumns?: ColumnConfig[];
	listEvent?: EventOutreachSchemaType | null;
	maxLength?: number;
}

/**
 * Subject editor for a campaign email/inmail step. For a follow-up email step
 * (one with a preceding email node) it shows the inherited "Re: …" subject as a
 * placeholder and a note that the subject is copied from the previous step but
 * can be overridden — shared by the List and Messages campaign editors.
 */
export const CampaignEmailSubjectEditor: FunctionComponent<CampaignEmailSubjectEditorProps> = ({
	nodeId,
	nodeType,
	subject,
	hasPrecedingEmailNode,
	eventBasedState,
	getPreviousNode,
	onChange,
	onInputChange,
	currentLeadIndex,
	listColumns,
	listEvent,
	maxLength,
}) => {
	const { t } = useTranslation();

	const isFollowUpEmail = hasPrecedingEmailNode && nodeType === "emailNode";
	const inheritedReplySubject = isFollowUpEmail
		? resolveInheritedReplySubject(nodeId, getPreviousNode, eventBasedState)
		: "";

	return (
		<SubjectEditor
			subject={subject}
			handleChange={onChange}
			handleInputChange={onInputChange}
			currentLeadIndex={currentLeadIndex}
			maxLength={maxLength}
			infoText={isFollowUpEmail ? t("campaign.nodePanel.subjectInheritedNote") : undefined}
			placeholderSubject={inheritedReplySubject || undefined}
			listColumns={listColumns}
			listEvent={listEvent}
		/>
	);
};
